/**
 * utils/successHandler.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The counterpart to errorHandler.ts: SINGLE SOURCE OF TRUTH for confirming a
 * write. Every service call that changes state ends here.
 *
 * The rule is the same as on the error side — show the BACKEND's message when
 * it sent one, never a string this app invented. sk-company-cash-api returns
 * the created or updated record plus, on several routes, a `message`; where it
 * does, that sentence is authoritative, because it knows what actually
 * happened (e.g. an idempotent replay returns 200 with "already recorded"
 * rather than 201).
 *
 * The `title` argument is the action in the product's own vocabulary — "Cash
 * assigned", "Spend recorded", "Variance routed" — and the fallback is what
 * shows when the API sent no sentence of its own.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { showToast, type ToastTone } from '../components/Toast';

const DEFAULT_FALLBACK = 'Done.';

/** The response shapes the API uses. `data` covers an axios response passed whole. */
interface SuccessLike {
  message?: string;
  created?: boolean;
  data?: { message?: string; created?: boolean };
}

const isUsable = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Pull the message out, in priority order:
 *   1. response.message        — the API's own sentence
 *   2. response.data.message   — an axios response handed over unwrapped
 *   3. fallback
 */
export function getSuccessMessage(response: unknown, fallback = DEFAULT_FALLBACK): string {
  if (!response || typeof response !== 'object') return fallback;
  const res = response as SuccessLike;
  if (isUsable(res.message)) return res.message.trim();
  if (isUsable(res.data?.message)) return res.data!.message!.trim();
  return fallback;
}

/**
 * Confirm a state change.
 *
 * `tone` defaults to `plain` — the design's white left border — because most
 * confirmations in this product are neutral facts ("Rule updated."), and
 * reserving green for the ones that actually close something out keeps the
 * green meaningful. Pass 'success' for a completion: cash assigned, a variance
 * routed, an approval decided.
 */
export function showSuccess(
  response: unknown,
  title: string,
  fallback = DEFAULT_FALLBACK,
  tone: ToastTone = 'plain',
): string {
  const message = getSuccessMessage(response, fallback);
  // Avoid a card that says the same thing twice: when the API's message IS the
  // title, show one line rather than an echo.
  showToast(tone, title, message === title ? undefined : message);
  return message;
}

/** A completed action — green rail. */
export const showCompleted = (response: unknown, title: string, fallback = DEFAULT_FALLBACK): string =>
  showSuccess(response, title, fallback, 'success');

/**
 * Client-side validation, where there is no backend response to quote. Kept
 * here rather than in errorHandler.ts because nothing failed — the user simply
 * has not finished the form. The design words these as instructions ("Give the
 * rule an amount.") rather than errors.
 */
export const showValidation = (message: string): void => showToast('danger', message);

/**
 * True when a write was a replay rather than a new record. The API returns
 * `created: false` with a 200 when an idempotency key has already been seen,
 * which lets a screen say "already recorded" instead of double-counting.
 */
export function wasCreated(response: unknown): boolean {
  const res = response as SuccessLike;
  return res?.created ?? res?.data?.created ?? true;
}
